import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';

import { ApiError, listCards, updateCardPin, type CardSummary } from '../api/client.ts';
import { STEP_UP_ACTIONS } from '../api/step-up.ts';
import { DigitGrid } from '../components/digit-grid.tsx';
import { IconChevronLeft } from '../components/icons.tsx';
import { PinKeypad } from '../components/pin-keypad.tsx';
import { LoadingState } from '../components/states.tsx';
import { showToast } from '../components/toast.tsx';
import { isStepUpCancelled, useStepUpGate } from '../lib/use-step-up.tsx';
import { usePlatform } from '../platform/index.ts';

import { rootRoute } from './__root.tsx';

const PIN_LENGTH = 4;

export interface ChangePinSearch {
  readonly cardId?: string;
}

type Stage = 'new' | 'confirm';

function copyForError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'PIN_CHANGE_VIRTUAL_CARD':
        return 'Virtual cards do not have a PIN.';
      case 'PIN_INVALID_FORMAT':
        return 'PIN must be 4 digits.';
      case 'PIN_REUSED':
        return 'Choose a PIN different from your previous one.';
      case 'CARD_NOT_FOUND':
        return 'We could not find this card. Go back and try again.';
      case 'RATE_LIMITED':
        return 'Too many attempts. Try again in a few minutes.';
      default:
        return err.message || 'Could not update PIN. Please try again.';
    }
  }
  return 'Could not update PIN. Please try again.';
}

const ChangePinPage = (): ReactNode => {
  const platform = usePlatform();
  const navigate = useNavigate();
  const { cardId } = useSearch({ from: changePinRoute.id });
  const [stage, setStage] = useState<Stage>('new');
  const [firstPin, setFirstPin] = useState<string>('');
  const [digits, setDigits] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const stepUp = useStepUpGate();

  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: ({ signal }) => listCards(signal) });
  const card: CardSummary | undefined = cardsQ.data?.cards.find((c) => c.id === cardId);

  useEffect(() => {
    if (cardId === undefined) {
      void navigate({ to: '/' });
      return;
    }
    if (cardsQ.isLoading) return;
    if (card === undefined) {
      void navigate({ to: '/' });
      return;
    }
    const status = (card.status ?? '').toUpperCase();
    const type = (card.type ?? '').toUpperCase();
    if (type !== 'PHYSICAL' || status !== 'ACTIVE') {
      void navigate({ to: '/' });
    }
  }, [cardId, card, cardsQ.isLoading, navigate]);

  const change = useMutation({
    mutationFn: (pin: string): Promise<unknown> => {
      if (cardId === undefined) return Promise.reject(new Error('missing cardId'));
      const id = cardId;
      return stepUp.guard(
        {
          action: STEP_UP_ACTIONS.CARD_PIN_CHANGE,
          params: { cardId: id },
          summary: `Set a new PIN for card •••• ${card?.lastFour ?? '????'}.`,
          onSetUpBiometrics: (): void => {
            void navigate({ to: '/security' });
          },
        },
        (grant) => updateCardPin(id, pin, grant),
      );
    },
    onSuccess: (): void => {
      platform.haptic('success');
      // Wipe the pin buffers before the navigation so a route-stack
      // snapshot in dev tools never holds the value.
      setFirstPin('');
      setDigits('');
      showToast('PIN updated.');
      void navigate({ to: '/' });
    },
    onError: (err: unknown): void => {
      // Closing the confirmation sheet is a decision, not a failure — clear the
      // entry and leave the user where they were, with nothing shouted at them.
      if (isStepUpCancelled(err)) {
        setFirstPin('');
        setDigits('');
        setStage('new');
        return;
      }
      platform.haptic('error');
      const message = copyForError(err);
      showToast(message);
      // Reset to stage 1 regardless of the failure so the user re-enters
      // both digits cleanly. PIN_CHANGE_VIRTUAL_CARD also navigates home
      // since the entry is meaningless on the wrong card type.
      setFirstPin('');
      setDigits('');
      setStage('new');
      setError(true);
      if (err instanceof ApiError && err.code === 'PIN_CHANGE_VIRTUAL_CARD') {
        void navigate({ to: '/' });
      }
    },
  });

  const onKey = (k: '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | 'del'): void => {
    if (change.isPending) return;
    if (error) setError(false);
    if (k === 'del') {
      setDigits((curr) => curr.slice(0, -1));
      return;
    }
    if (digits.length >= PIN_LENGTH) return;
    const next = digits + k;
    setDigits(next);
    if (next.length < PIN_LENGTH) return;
    if (stage === 'new') {
      setFirstPin(next);
      setDigits('');
      setStage('confirm');
      return;
    }
    if (next !== firstPin) {
      platform.haptic('error');
      showToast("PINs don't match. Try again.");
      setError(true);
      // Keep the filled digits visible while the 200ms shake plays
      // — clearing the value before the animation runs would shake
      // empty cells, which reads as "nothing happened". After the
      // shake settles, drop back to stage 1 with everything cleared
      // so the user retypes both halves.
      window.setTimeout(() => {
        setFirstPin('');
        setDigits('');
        setStage('new');
        setError(false);
      }, 300);
      return;
    }
    change.mutate(next);
  };

  if (cardsQ.isLoading || (cardId !== undefined && card === undefined && !cardsQ.isError)) {
    return <LoadingState label="Loading your card" />;
  }

  const title = stage === 'new' ? 'Choose a new PIN' : 'Confirm your PIN';
  const stepLabel = stage === 'new' ? 'Step 1 of 2' : 'Step 2 of 2';

  return (
    <main className="screen">
      <header className="topbar">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={(): void => {
            void navigate({ to: '/' });
          }}
        >
          <IconChevronLeft />
        </button>
        <h1 className="topbar-title">Change PIN</h1>
        <span className="topbar-spacer" aria-hidden="true" />
      </header>
      <section className="change-pin-body">
        <p className="change-pin-step">{stepLabel}</p>
        <p className="change-pin-intro">{title}</p>
        <div className="change-pin-stage-indicator" role="status" aria-label={stepLabel}>
          <span className={stage === 'new' ? 'dot active' : 'dot done'} />
          <span className={stage === 'confirm' ? 'dot active' : 'dot'} />
        </div>
        <DigitGrid value={digits} length={PIN_LENGTH} masked error={error} />
        <PinKeypad onKey={onKey} disabled={change.isPending} />
        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {String(digits.length)} of {String(PIN_LENGTH)} digits entered
        </p>
      </section>
      {stepUp.sheet}
    </main>
  );
};

export const changePinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/change-pin',
  component: ChangePinPage,
  validateSearch: (s: Record<string, unknown>): ChangePinSearch =>
    typeof s.cardId === 'string' ? { cardId: s.cardId } : {},
});
